import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, X, Download, DollarSign, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { createPaymentsFromExcel, validateExcelPaymentsData, getUsersForExcelUpload } from '../../Fetch/fetchPayment';
import styles from './PaymentExcelUpload.module.css';
import * as XLSX from 'xlsx';

const PaymentExcelUpload = ({ onClose, onPaymentsCreated, buildingId }) => {
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]); // Store all raw data
  const [excelData, setExcelData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0); // Header row selection
  const [columnMapping, setColumnMapping] = useState({
    tenantName: '', // Tenant name
    apartmentNumber: '', // Apartment number
    paymentType: '',
    amount: '',
    paymentDate: '',
    paymentMethod: '',
    description: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ success: [], errors: [] });
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Header Selection, 3: Mapping, 4: Processing, 5: Results
  const [users, setUsers] = useState([]); // Store users data for ID lookup

  // Fetch users when component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        console.log('🔄 Fetching users for building:', buildingId);
        const usersData = await getUsersForExcelUpload(buildingId);
        console.log('✅ Fetched users data:', usersData);
        setUsers(usersData || []);
        
        // Make users available for debugging
        window.debugUsersData = usersData;
        window.debugUsers = () => {
          console.log('=== USERS DEBUG INFO ===');
          console.log('Users array:', usersData);
          console.log('Users count:', usersData?.length || 0);
          
          if (usersData && usersData.length > 0) {
            console.log('First user structure:', usersData[0]);
            console.log('Available properties:', Object.keys(usersData[0]));
            
            console.log('All users:');
            usersData.forEach((user, index) => {
              console.log(`${index + 1}. Name: ${user.fullName}, Apartment: ${user.apartmentNumber}, ID: ${user.userId}`);
            });
          } else {
            console.log('No users found');
          }
          console.log('=== END DEBUG ===');
        };
        
      } catch (error) {
        console.error('❌ Failed to fetch users:', error);
        setUsers([]);
      }
    };

    if (buildingId) {
      fetchUsers();
    }
  }, [buildingId]);

  // Helper function to get user ID by name and/or apartment number
  const getUserId = (tenantName, apartmentNumber) => {
    console.log('🔍 getUserId called with:', { tenantName, apartmentNumber });
    console.log('🔍 Users array length:', users.length);
    
    if (users.length === 0) {
      console.log('❌ No users loaded');
      return null;
    }

    let user = null;

    // Try exact match first (both name and apartment)
    if (tenantName && apartmentNumber) {
      user = users.find(u => {
        const nameMatch = u.fullName === tenantName;
        const apartmentMatch = String(u.apartmentNumber) === String(apartmentNumber) ||
                              String(u.number) === String(apartmentNumber);
        
        console.log('🔍 Checking user:', {
          userName: u.fullName,
          userApartment: u.apartmentNumber || u.number,
          nameMatch,
          apartmentMatch
        });
        
        return nameMatch && apartmentMatch;
      });
      
      if (user) {
        console.log('✅ Found exact match:', user);
        return user.userId;
      }
    }

    // Try name match only
    if (tenantName) {
      user = users.find(u => u.fullName === tenantName);
      
      if (user) {
        console.log('✅ Found name match:', user);
        return user.userId;
      }
    }

    // Try apartment match only
    if (apartmentNumber) {
      user = users.find(u => 
        String(u.apartmentNumber) === String(apartmentNumber) ||
        String(u.number) === String(apartmentNumber)
      );
      
      if (user) {
        console.log('✅ Found apartment match:', user);
        return user.userId;
      }
    }

    console.log('❌ No user found for:', { tenantName, apartmentNumber });
    console.log('📋 Available users:', users.map(u => ({
      name: u.fullName,
      apartment: u.apartmentNumber,
      id: u.userId
    })));
    
    return null;
  };

  // Update headers and data when header row selection changes
  const updateHeadersAndData = useCallback((data, headerIndex) => {
    if (data.length <= headerIndex) return;

    const headerRow = data[headerIndex].map(header =>
      header ? String(header).trim() : ''
    );

    const dataRows = data.slice(headerIndex + 1)
      .filter(row => row && row.some(cell => cell !== undefined && cell !== null && cell !== ''))
      .map(row => row.map(cell =>
        cell !== undefined && cell !== null ? String(cell).trim() : ''
      ));

    setHeaders(headerRow);
    setExcelData(dataRows);
  }, []);

  // Handle header row selection change
  const handleHeaderRowChange = (newHeaderIndex) => {
    setHeaderRowIndex(newHeaderIndex);
    updateHeadersAndData(rawData, newHeaderIndex);
    // Reset column mapping when header row changes
    setColumnMapping({
      tenantName: '',
      apartmentNumber: '',
      paymentType: '',
      amount: '',
      paymentDate: '',
      paymentMethod: '',
      description: ''
    });
  };

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.match(/\.(xlsx|xls|csv)$/)) {
      alert('אנא בחר קובץ Excel (.xlsx, .xls) או CSV (.csv)');
      return;
    }

    setFile(uploadedFile);

    try {
      let processedData = [];

      if (uploadedFile.name.match(/\.csv$/)) {
        // Handle CSV files
        const text = await uploadedFile.text();
        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('הקובץ חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
          return;
        }

        processedData = lines.map(line =>
          line.split(',').map(cell => cell.trim().replace(/"/g, ''))
        ).filter(row => row.some(cell => cell !== ''));

      } else if (uploadedFile.name.match(/\.xlsx?$/)) {
        // Handle Excel files using xlsx library
        try {
          const arrayBuffer = await uploadedFile.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });

          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON array
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            alert('הקובץ חייב להכיל לפחות שורת כותרת ושורת נתונים אחת');
            return;
          }

          processedData = jsonData;

        } catch (error) {
          alert('שגיאה בקריאת קובץ ה-Excel. אנא בדוק שהקובץ תקין');
          return;
        }
      }

      // Store raw data and initialize with first row as header
      setRawData(processedData);
      setHeaderRowIndex(0);
      updateHeadersAndData(processedData, 0);
      setCurrentStep(2); // Go to header selection step

    } catch (error) {
      alert('שגיאה בקריאת הקובץ');
    }
  }, [updateHeadersAndData]);

  // Convert Excel serial date to JavaScript Date
  const excelSerialDateToJSDate = (serial) => {
    // Excel serial date system:
    // - Excel stores dates as numbers representing days since December 30, 1899
    // - Serial 1 = January 1, 1900
    // - Serial 2 = January 2, 1900, etc.
    
    // Known test values:
    // 44927 = January 1, 2023
    // 45292 = January 1, 2024
    // 45658 = January 1, 2025
    // 45901 = August 1, 2025
    
    console.log(`🔢 Converting Excel serial: ${serial}`);
    
    // Excel's epoch: December 31, 1899 at midnight
    // Serial 1 = January 1, 1900
    // So we start from Dec 31, 1899 and add (serial - 1) days
    const excelEpoch = new Date(1899, 11, 31); // December 31, 1899
    
    // Adjust for Excel's leap year bug
    // Excel incorrectly considers 1900 a leap year, so it has Feb 29, 1900
    // We need to subtract 1 day for all dates after Feb 28, 1900 (serial 60)
    let daysToAdd = serial - 1; // Subtract 1 because serial 1 = Jan 1, 1900
    if (serial >= 61) { // Serial 61 = March 1, 1900
      daysToAdd = serial - 2; // Subtract an extra day for the leap year bug
    }
    
    // Calculate the date by adding days to epoch
    const msPerDay = 24 * 60 * 60 * 1000;
    const resultDate = new Date(excelEpoch.getTime() + (daysToAdd * msPerDay));
    
    // Format the date for logging
    const formattedDate = `${resultDate.getDate()}/${resultDate.getMonth() + 1}/${resultDate.getFullYear()}`;
    
    // Manual verification for debugging
    // Test with known Excel serial dates
    if (serial === 45658) {
      const testDate = new Date(2025, 0, 1); // Jan 1, 2025
      const diff = Math.abs(resultDate.getTime() - testDate.getTime()) / msPerDay;
      if (diff > 1) {
        console.error(`❌ Serial 45658 conversion error! Expected Jan 1, 2025, got ${formattedDate}`);
      }
    } else if (serial === 45657) {
      console.log('📍 Serial 45657 detected - this should be Dec 31, 2024');
      const testDate = new Date(2024, 11, 31); // Dec 31, 2024
      const diff = Math.abs(resultDate.getTime() - testDate.getTime()) / msPerDay;
      if (diff > 1) {
        console.error(`❌ Serial 45657 conversion error! Expected Dec 31, 2024, got ${formattedDate}`);
      }
    } else if (serial >= 45627 && serial <= 45657) {
      console.log(`📅 Serial ${serial} is in December 2024 range`);
    }
    
    // Log the result
    console.log(`📊 Excel serial ${serial} converted to: ${formattedDate} (ISO: ${resultDate.toISOString()})`);
    
    // Verify the year makes sense
    const year = resultDate.getFullYear();
    if (year < 2020 || year > 2030) {
      console.warn(`⚠️ Unexpected year ${year} from serial ${serial}. Check date conversion!`);
    }
    
    return resultDate;
  };

  // Parse date from various formats
  const parseDate = (dateString) => {
    if (!dateString) {
      console.warn('⚠️ No date provided, defaulting to current date');
      return new Date().toISOString();
    }
    
    console.log('📅 Parsing date string:', dateString);
    
    // Special check for problematic values
    if (dateString && (dateString === '45657' || dateString === '45656')) {
      console.warn(`🔍 Found potentially problematic serial: ${dateString} - this will convert to Dec 2024`);
    }
    
    const dateStr = dateString.toString().trim();
    
    // First, try standard date formats before checking for serial dates
    const formats = [
      { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, type: 'DD/MM/YYYY' }, // DD/MM/YYYY
      { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, type: 'DD-MM-YYYY' }, // DD-MM-YYYY
      { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, type: 'YYYY-MM-DD' }, // YYYY-MM-DD
      { pattern: /^(\d{1,2})\/(\d{4})$/, type: 'MM/YYYY' }, // MM/YYYY
      { pattern: /^(\d{1,2})-(\d{4})$/, type: 'MM-YYYY' }, // MM-YYYY
    ];
    
    // Try format patterns first
    for (const format of formats) {
      const match = dateStr.match(format.pattern);
      if (match) {
        console.log('📝 Detected regular date format:', format.type);
        let parsedDate;
        
        if (format.type === 'YYYY-MM-DD') {
          // YYYY-MM-DD format
          parsedDate = new Date(match[1], match[2] - 1, match[3]);
        } else if (format.type === 'MM/YYYY' || format.type === 'MM-YYYY') {
          // MM/YYYY or MM-YYYY format - set to first day of month
          parsedDate = new Date(match[2], match[1] - 1, 1);
        } else {
          // DD/MM/YYYY or DD-MM-YYYY format
          parsedDate = new Date(match[3], match[2] - 1, match[1]);
        }
        
        console.log('✅ Parsed regular date successfully:', parsedDate.toISOString());
        return parsedDate.toISOString();
      }
    }
    
    // If no pattern matched, check if it's an Excel serial date
    const numericValue = parseFloat(dateStr);
    // Excel dates typically range from 1 (1/1/1900) to ~50000 (for dates around 2036)
    // Common range for current dates: 44000-46000 (2020-2025)
    if (!isNaN(numericValue) && 
        numericValue >= 1 && 
        numericValue < 50000 && 
        !dateStr.includes('/') && 
        !dateStr.includes('-') && 
        !dateStr.includes('.') && 
        !dateStr.includes(' ')) {
      console.log('📊 Detected Excel serial date:', numericValue);
      
      // Check if this looks like a reasonable date range (1900-2100)
      // Excel serial 44197 = Jan 1, 2021
      // Excel serial 45658 = Jan 1, 2025
      // Excel serial 47119 = Jan 1, 2029
      if (numericValue < 1 || numericValue > 73415) { // 73415 = year 2100
        console.warn('⚠️ Serial number out of reasonable date range:', numericValue);
      }
      
      try {
        const jsDate = excelSerialDateToJSDate(numericValue);
        return jsDate.toISOString();
      } catch (error) {
        console.error('❌ Failed to convert Excel serial date:', error);
      }
    }
    
    // Try to parse as-is for other formats
    if (isNaN(numericValue)) {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        console.log('✅ Parsed date as-is:', date.toISOString());
        return date.toISOString();
      }
    }
    
    // Default to current date if parsing fails
    console.warn('❌ Date parsing failed, defaulting to current date. Original:', dateString);
    return new Date().toISOString();
  };

  // Map payment method
  const mapPaymentMethod = (method) => {
    if (!method) return 'מזומן';
    
    const methodLower = method.toLowerCase();
    if (methodLower.includes('cash') || methodLower.includes('מזומן')) return 'מזומן';
    if (methodLower.includes('check') || methodLower.includes('צ\'ק') || methodLower.includes('צק')) return 'צ\'ק';
    if (methodLower.includes('bank') || methodLower.includes('העברה') || methodLower.includes('בנקאית')) return 'העברה בנקאית';
    if (methodLower.includes('credit') || methodLower.includes('אשראי')) return 'Credit Card';
    
    return 'מזומן'; // Default
  };

  // Process payments
  const processPayments = async () => {
    // Required fields validation
    if (!columnMapping.amount || !columnMapping.paymentType) {
      alert('חובה לבחור עמודות עבור: סכום וסוג תשלום');
      return;
    }
    
    if (!columnMapping.tenantName && !columnMapping.apartmentNumber) {
      alert('חובה לבחור עמודה עבור שם דייר או מספר דירה (או שניהם)');
      return;
    }

    setIsProcessing(true);
    setCurrentStep(4); // Processing step
    const totalRows = excelData.length;
    setProgress({ current: 0, total: totalRows });

    try {
      // Prepare data for bulk creation
      const paymentsToCreate = [];
      let processedCount = 0;

      // Map Excel data to payment objects
      const tenantNameIndex = columnMapping.tenantName ?
        headers.findIndex(h => h === columnMapping.tenantName) : -1;
      const apartmentIndex = columnMapping.apartmentNumber ?
        headers.findIndex(h => h === columnMapping.apartmentNumber) : -1;
      const typeIndex = headers.findIndex(h => h === columnMapping.paymentType);
      const amountIndex = headers.findIndex(h => h === columnMapping.amount);
      const dateIndex = columnMapping.paymentDate ?
        headers.findIndex(h => h === columnMapping.paymentDate) : -1;
      const methodIndex = columnMapping.paymentMethod ?
        headers.findIndex(h => h === columnMapping.paymentMethod) : -1;
      const descriptionIndex = columnMapping.description ?
        headers.findIndex(h => h === columnMapping.description) : -1;

      // Process each row to create payment data objects
      for (let i = 0; i < excelData.length; i++) {
        const row = excelData[i];
        
        // Update progress for every 5 rows to make it smoother
        if (i % 5 === 0 || i === excelData.length - 1) {
          setProgress({ current: i + 1, total: totalRows });
          // Allow UI to update
          await new Promise(resolve => setTimeout(resolve, 0));
        }

        const tenantName = tenantNameIndex >= 0 ? row[tenantNameIndex]?.toString().trim() : '';
        const apartmentNumber = apartmentIndex >= 0 ? row[apartmentIndex]?.toString().trim() : '';
        const paymentType = row[typeIndex]?.toString().trim();
        const amountStr = row[amountIndex]?.toString().trim();
        const paymentDate = dateIndex >= 0 ? row[dateIndex]?.toString().trim() : '';
        const paymentMethod = methodIndex >= 0 ? row[methodIndex]?.toString().trim() : '';
        const description = descriptionIndex >= 0 ? row[descriptionIndex]?.toString().trim() : '';

        // Parse amount
        const amount = parseFloat(amountStr.replace(/[^0-9.-]/g, ''));
        if (isNaN(amount) || amount <= 0) {
          continue; // Skip invalid amounts
        }

        // Get user ID
        const userId = getUserId(tenantName, apartmentNumber);
        if (!userId) {
          // Add to errors if user not found
          const userDisplay = tenantName && apartmentNumber ? `${tenantName} (דירה ${apartmentNumber})` :
                              tenantName ? tenantName : 
                              apartmentNumber ? `דירה ${apartmentNumber}` : 'לא צוין';
          paymentsToCreate.push({
            _error: true,
            _rowIndex: i,
            _originalRowNumber: i + headerRowIndex + 2,
            userValue: userDisplay,
            error: `משתמש לא נמצא: ${userDisplay}`
          });
          continue;
        }

        let parsedPaymentDate = parseDate(paymentDate);
        
        // Auto-fix: If description says January but date is December 2024, adjust to January 2025
        if (description && description.includes('ינואר') && parsedPaymentDate.includes('2024-12')) {
          console.warn(`🔧 Auto-fixing January payment with Dec 2024 date to Jan 2025`);
          const adjustedDate = new Date(2025, 0, 1); // January 1, 2025
          parsedPaymentDate = adjustedDate.toISOString();
        }
        
        const paymentData = {
          UserId: userId,
          PaymentType: paymentType,
          Amount: amount,
          PaymentDate: parsedPaymentDate,
          PaymentMethod: mapPaymentMethod(paymentMethod),
          Description: description || '',
          BuildingId: buildingId,
          Status: 'שולם',
          // Add row index for error tracking
          _rowIndex: i,
          _originalRowNumber: i + headerRowIndex + 2,
          _userValue: tenantName && apartmentNumber ? `${tenantName} (דירה ${apartmentNumber})` :
                      tenantName ? tenantName : 
                      apartmentNumber ? `דירה ${apartmentNumber}` : 'לא צוין'
        };

        // Log payment data for debugging
        console.log(`💳 Row ${i + 1} payment:`, {
          user: paymentData._userValue,
          amount: paymentData.Amount,
          type: paymentData.PaymentType,
          originalDate: paymentDate,
          parsedDate: parsedPaymentDate,
          dateMonth: new Date(parsedPaymentDate).getMonth() + 1,
          dateYear: new Date(parsedPaymentDate).getFullYear()
        });

        paymentsToCreate.push(paymentData);
      }

      console.log('Payments to create:', paymentsToCreate);

      // Separate errors from valid payments
      const errors = paymentsToCreate.filter(p => p._error);
      const validPayments = paymentsToCreate.filter(p => !p._error);

      if (validPayments.length === 0) {
        setResults({
          success: [],
          errors: errors.map(e => ({
            row: e._originalRowNumber,
            user: e.userValue,
            error: e.error
          }))
        });
        setIsProcessing(false);
        setCurrentStep(5);
        return;
      }

      // Show that we're processing the data
      setProgress({ current: totalRows, total: totalRows + 1 }); // Show almost complete
      
      // Add small delay to show progress before API call
      await new Promise(resolve => setTimeout(resolve, 100));

      // Call the bulk API (temporarily use old format until backend supports new structure)
      const result = await createPaymentsFromExcel(validPayments);

      console.log('Bulk creation result:', result);

      // Update progress to show completion
      setProgress({ current: totalRows + 1, total: totalRows + 1 });

      // Process the results from the bulk API
      const successResults = [];
      const errorResults = [...errors.map(e => ({
        row: e._originalRowNumber,
        user: e.userValue,
        error: e.error
      }))];

      // Handle success results
      if (result.successes && Array.isArray(result.successes)) {
        result.successes.forEach(success => {
          const originalPayment = validPayments[success.paymentIndex - 1];
          successResults.push({
            row: originalPayment?._originalRowNumber || success.paymentIndex,
            user: originalPayment?._userValue || 'לא ידוע',
            amount: success.Amount,
            paymentType: success.PaymentType,
            paymentMethod: success.PaymentMethod,
            paymentDate: success.PaymentDate,
            message: 'נוצר בהצלחה'
          });
        });
      }

      // Handle error results
      if (result.errors && Array.isArray(result.errors)) {
        result.errors.forEach(error => {
          errorResults.push({
            row: error.paymentIndex,
            user: error.user || 'לא ידוע',
            amount: error.amount || 0,
            error: error.error
          });
        });
      }

      setResults({ success: successResults, errors: errorResults });
      setIsProcessing(false);
      setCurrentStep(5); // Results step

      // Notify parent component to refresh payments list
      if (successResults.length > 0) {
        onPaymentsCreated();
      }

    } catch (error) {
      console.error('Bulk payment creation error:', error);

      const errorMessage = error.message || 'שגיאה בלתי צפויה ביצירת התשלומים';

      setResults({
        success: [],
        errors: [{
          row: 'כללי',
          user: 'שגיאה כללית',
          amount: '',
          error: errorMessage
        }]
      });

      setIsProcessing(false);
      setCurrentStep(5); // Results step

      alert(`שגיאה ביצירת התשלומים: ${errorMessage}`);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const csvContent = [
      ['שם דייר', 'מספר דירה', 'סוג תשלום', 'סכום', 'תאריך תשלום', 'אמצעי תשלום', 'תיאור'],
      ['יוסי כהן', '12', 'ועד בית', '350', '01/01/2024', 'מזומן', 'תשלום ינואר'],
      ['רחל לוי', '15', 'תיקונים', '150', '15/01/2024', 'העברה בנקאית', 'השתתפות בתיקון מעלית'],
      ['דוד ישראלי', '8', 'ועד בית', '350', '01/01/2024', 'צ\'ק', 'תשלום ינואר']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'template_payments.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetUpload = () => {
    setFile(null);
    setRawData([]);
    setExcelData([]);
    setHeaders([]);
    setHeaderRowIndex(0);
    setColumnMapping({
      tenantName: '',
      apartmentNumber: '',
      paymentType: '',
      amount: '',
      paymentDate: '',
      paymentMethod: '',
      description: ''
    });
    setCurrentStep(1);
    setResults({ success: [], errors: [] });
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            <FileSpreadsheet size={24} />
            העלאת תשלומים מקובץ Excel או CSV
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && currentStep === 3 && (
          <div style={{ background: '#f0f0f0', padding: '10px', margin: '10px', borderRadius: '5px' }}>
            <h4>🔧 Debug Info:</h4>
            <p>Users loaded: {users.length}</p>
            <p>Building ID: {buildingId}</p>
            <button 
              onClick={() => window.debugUsers && window.debugUsers()} 
              style={{ background: '#007bff', color: 'white', padding: '5px', border: 'none', borderRadius: '3px' }}
            >
              🔍 Debug Users in Console
            </button>
          </div>
        )}

        {/* Rest of your JSX remains the same - Step 1: File Upload */}
        {currentStep === 1 && (
          <div className={styles.content}>
            <div className={styles.uploadSection}>
              <div className={styles.instructions}>
                <h3>הוראות:</h3>
                <ul>
                  <li>קובץ CSV או Excel חייב להכיל שורת כותרות</li>
                  <li>חובה: סוג תשלום, סכום</li>
                  <li>חובה (לפחות אחד): שם דייר או מספר דירה (או שניהם)</li>
                  <li>אופציונלי: תאריך תשלום, אמצעי תשלום, תיאור</li>
                  <li>סוגי תשלום: "ועד בית", "תיקונים", "אחר"</li>
                  <li>אמצעי תשלום: "מזומן", "צ'ק", "העברה בנקאית", "כרטיס אשראי"</li>
                  <li>תאריך: בפורמט DD/MM/YYYY או DD-MM-YYYY</li>
                  <li>אם לא צוין תאריך, ישתמש בתאריך היום</li>
                  <li>אם לא צוין אמצעי תשלום, ברירת המחדל היא "מזומן"</li>
                  <li>אם צוינו גם שם דייר וגם מספר דירה, המערכת תנסה להתאים בדיוק</li>
                </ul>
              </div>

              <div className={styles.uploadArea}>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className={styles.fileInput}
                  id="payment-excel-upload"
                />
                <label htmlFor="payment-excel-upload" className={styles.uploadLabel}>
                  <Upload size={48} />
                  <span>לחץ לבחירת קובץ CSV או Excel</span>
                  <small>קבצים נתמכים: .csv, .xlsx, .xls</small>
                </label>
              </div>

              <div className={styles.templateSection}>
                <button className={styles.templateButton} onClick={downloadTemplate}>
                  <Download size={16} />
                  הורד תבנית CSV לדוגמה
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Header Row Selection */}
        {currentStep === 2 && (
          <div className={styles.content}>
            <div className={styles.headerSelection}>
              <h3>בחר שורת כותרות</h3>
              <div className={styles.fileInfo}>
                <strong>קובץ:</strong> {file?.name} | <strong>סה"כ שורות:</strong> {rawData.length}
              </div>

              <div className={styles.headerRowSelector}>
                <label>שורת כותרות (מתחילה מ-1):</label>
                <select
                  value={headerRowIndex + 1}
                  onChange={(e) => handleHeaderRowChange(parseInt(e.target.value) - 1)}
                  className={styles.columnSelect}
                >
                  {rawData.map((_, index) => (
                    <option key={index} value={index + 1}>
                      שורה {index + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview of selected header row and data */}
              <div className={styles.headerPreview}>
                <h4>תצוגה מקדימה של הקובץ:</h4>
                <div className={styles.previewContainer}>
                  <div className={styles.previewTable}>
                    <table>
                      <tbody>
                        {rawData.slice(0, Math.min(10, rawData.length)).map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex === headerRowIndex ? styles.headerRow : ''}>
                            <td className={styles.rowNumber}>
                              {rowIndex + 1}
                              {rowIndex === headerRowIndex && <span className={styles.headerIndicator}> (כותרות)</span>}
                            </td>
                            {row.slice(0, 8).map((cell, cellIndex) => (
                              <td key={cellIndex} className={rowIndex === headerRowIndex ? styles.headerCell : ''}>
                                {cell || '(ריק)'}
                              </td>
                            ))}
                            {row.length > 8 && <td>...</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {headers.length > 0 && (
                  <div className={styles.selectedHeaders}>
                    <h4>כותרות שנבחרו:</h4>
                    <div className={styles.headersList}>
                      {headers.map((header, index) => (
                        <span key={index} className={styles.headerTag}>
                          {header || `עמודה ${index + 1}`}
                        </span>
                      ))}
                    </div>
                    <p className={styles.dataInfo}>
                      <strong>שורות נתונים זמינות:</strong> {excelData.length}
                    </p>
                  </div>
                )}
              </div>

              <div className={styles.actions}>
                <button className={styles.backButton} onClick={resetUpload}>
                  חזור
                </button>
                <button
                  className={styles.processButton}
                  onClick={() => setCurrentStep(3)}
                  disabled={headers.length === 0 || excelData.length === 0}
                >
                  המשך להתאמת עמודות
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Column Mapping */}
        {currentStep === 3 && (
          <div className={styles.content}>
            <div className={styles.mappingSection}>
              <h3>בחר התאמת עמודות</h3>
              <div className={styles.fileInfo}>
                <strong>קובץ:</strong> {file?.name} |
                <strong> שורת כותרות:</strong> {headerRowIndex + 1} |
                <strong> שורות נתונים:</strong> {excelData.length}
              </div>

              <div className={styles.mappingGrid}>
                <div className={styles.mappingRow}>
                  <label>שם דייר (אופציונלי):</label>
                  <select
                    value={columnMapping.tenantName}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, tenantName: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>מספר דירה (אופציונלי):</label>
                  <select
                    value={columnMapping.apartmentNumber}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, apartmentNumber: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label className={styles.required}>סוג תשלום (חובה):</label>
                  <select
                    value={columnMapping.paymentType}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, paymentType: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label className={styles.required}>סכום (חובה):</label>
                  <select
                    value={columnMapping.amount}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, amount: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>תאריך תשלום (אופציונלי):</label>
                  <select
                    value={columnMapping.paymentDate}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, paymentDate: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>אמצעי תשלום (אופציונלי):</label>
                  <select
                    value={columnMapping.paymentMethod}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.mappingRow}>
                  <label>תיאור (אופציונלי):</label>
                  <select
                    value={columnMapping.description}
                    onChange={(e) => setColumnMapping(prev => ({ ...prev, description: e.target.value }))}
                    className={styles.columnSelect}
                  >
                    <option value="">בחר עמודה</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>{header || `עמודה ${index + 1}`}</option>
                    ))}
                  </select>
                </div>

              </div>

              {/* Preview */}
              <div className={styles.preview}>
                <h4>תצוגה מקדימה (3 שורות ראשונות):</h4>
                <div className={styles.previewTable}>
                  <table>
                    <thead>
                      <tr>
                        <th>שם דייר</th>
                        <th>מספר דירה</th>
                        <th>סוג תשלום</th>
                        <th>סכום</th>
                        <th>תאריך</th>
                        <th>אמצעי</th>
                        <th>תיאור</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelData.slice(0, 3).map((row, index) => {
                        const tenantIdx = columnMapping.tenantName ?
                          headers.findIndex(h => h === columnMapping.tenantName) : -1;
                        const apartmentIdx = columnMapping.apartmentNumber ?
                          headers.findIndex(h => h === columnMapping.apartmentNumber) : -1;
                        const typeIdx = headers.findIndex(h => h === columnMapping.paymentType);
                        const amountIdx = headers.findIndex(h => h === columnMapping.amount);
                        const dateIdx = columnMapping.paymentDate ?
                          headers.findIndex(h => h === columnMapping.paymentDate) : -1;
                        const methodIdx = columnMapping.paymentMethod ?
                          headers.findIndex(h => h === columnMapping.paymentMethod) : -1;
                        const descIdx = columnMapping.description ?
                          headers.findIndex(h => h === columnMapping.description) : -1;

                        return (
                          <tr key={index}>
                            <td>{tenantIdx >= 0 ? (row[tenantIdx] || 'ריק') : 'לא נבחר'}</td>
                            <td>{apartmentIdx >= 0 ? (row[apartmentIdx] || 'ריק') : 'לא נבחר'}</td>
                            <td>{typeIdx >= 0 ? row[typeIdx] || 'חסר' : 'לא נבחר'}</td>
                            <td>{amountIdx >= 0 ? row[amountIdx] || 'חסר' : 'לא נבחר'}</td>
                            <td>{dateIdx >= 0 ? (row[dateIdx] || 'היום') : 'היום'}</td>
                            <td>{methodIdx >= 0 ? (row[methodIdx] || 'מזומן') : 'מזומן'}</td>
                            <td>{descIdx >= 0 ? (row[descIdx] || 'ריק') : 'לא נבחר'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={() => setCurrentStep(2)}>
                חזור
              </button>
              <button
                className={styles.processButton}
                onClick={processPayments}
                disabled={(!columnMapping.tenantName && !columnMapping.apartmentNumber) || !columnMapping.paymentType || !columnMapping.amount}
              >
                <DollarSign size={16} />
                יצירת {excelData.length} תשלומים
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Processing */}
        {currentStep === 4 && (
          <div className={styles.content}>
            <div className={styles.processing}>
              <div className={styles.progressSection}>
                <h3>יוצר תשלומים...</h3>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  ></div>
                </div>
                <div className={styles.progressText}>
                  {progress.current === progress.total && progress.total > 0 
                    ? 'שולח לשרת...' 
                    : `מעבד ${progress.current} מתוך ${progress.total} שורות`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Results */}
        {currentStep === 5 && (
          <div className={styles.content}>
            <div className={styles.results}>
              <h3>תוצאות העלאה</h3>

              <div className={styles.resultsSummary}>
                <div className={`${styles.summaryCard} ${styles.success}`}>
                  <CheckCircle size={20} />
                  <span>נוצרו בהצלחה: {results.success.length}</span>
                </div>
                <div className={`${styles.summaryCard} ${styles.error}`}>
                  <XCircle size={20} />
                  <span>שגיאות: {results.errors.length}</span>
                </div>
              </div>

              {/* Success results section */}
              <div className={styles.successResults}>
                <h4>
                  <CheckCircle size={16} />
                  תשלומים שנוצרו בהצלחה ({results.success.length})
                </h4>
                <div className={styles.resultsList}>
                  {results.success.length > 0 ? (
                    results.success.map((result, index) => (
                      <div key={index} className={styles.resultItem}>
                        <span>{result.user} - {result.paymentType}: ₪{result.amount}</span>
                        <small>
                          {result.paymentMethod}, {new Date(result.paymentDate).toLocaleDateString('he-IL')}
                        </small>
                      </div>
                    ))
                  ) : (
                    <div className={styles.resultItem}>
                      <span style={{ color: '#999', fontStyle: 'italic' }}>לא נוצרו תשלומים</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Errors section */}
              <div className={styles.errorResults}>
                <h4>
                  <AlertCircle size={16} />
                  שגיאות ({results.errors.length})
                </h4>
                <div className={styles.resultsList}>
                  {results.errors.length > 0 ? (
                    results.errors.map((error, index) => (
                      <div key={index} className={styles.resultItem}>
                        <span>שורה {error.row}: {error.user}</span>
                        <small className={styles.errorMessage}>{error.error}</small>
                      </div>
                    ))
                  ) : (
                    <div className={styles.resultItem}>
                      <span style={{ color: '#28a745', fontStyle: 'italic' }}>אין שגיאות - כל התשלומים נוצרו בהצלחה!</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button className={styles.backButton} onClick={resetUpload}>
                העלאה חדשה
              </button>
              <button className={styles.closeButton} onClick={onClose}>
                סגור
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentExcelUpload;